import type { StaticContext } from '@atlassian/forge-conditions';
import { type Extension } from '@atlassian/forge-ui';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';
import { fg } from '@confluence/feature-gating';

import { preloadUseStaticContext } from '../display-conditions/preloadUseStaticContext';
import { filterExtensionsByDisplayConditions } from '../display-conditions/useExtensionsFilteredByDisplayConditions';

import { supportsAccessNarrowing, type UseExtensionListProps } from './useExtensionList';
import { preloadExtensions } from './preloadExtensions';
// eslint-disable-next-line import/no-cycle
import { preloadForgeApps } from './preloadForgeApps';

export const filterExtensions = async (
	staticContext: StaticContext,
	extensions: Extension[],
	contentId: string | undefined,
	spaceKey: string,
	moduleType: string,
) => {
	// We suspend all GraphQL requests once React rendering starts.
	// However, this would cause the query here to never resolve, making the sidebar wait forever.
	// The preload flag tells next/packages/graphql/src/links/SSRLink.ts to allow this query since it is part of preloading.
	const fetchContext = { contentId, spaceKey, preload: true };
	const filteredResult = await filterExtensionsByDisplayConditions(
		extensions,
		staticContext,
		fetchContext,
	);

	setWindowExtensions(moduleType, filteredResult.extsToDisplay, filteredResult.conditionsError);
};

const setWindowExtensions = (
	moduleType: string,
	extensions: Extension[],
	error: Error | undefined,
) => {
	window.__SSR_FORGE_EXTENSIONS__ = window.__SSR_FORGE_EXTENSIONS__ || {};
	window.__SSR_FORGE_EXTENSIONS__[moduleType] = {
		extensions,
		error,
	};
};

export async function preloadUseExtensionList({
	contentId,
	spaceKey,
	moduleType,
	queryOptions,
	expandAppOwner = false,
	skipFilter = false,
}: UseExtensionListProps & {
	contentId?: string;
	spaceKey: string;
	skipFilter?: boolean;
}) {
	const { cloudId, activationId, isLoggedIn, accessStatus, locale } = getPreloaderFnContext();

	const hasAccessNarrowingSupport = supportsAccessNarrowing(moduleType);
	const isAnonymous = !isLoggedIn;
	const isExternalCollaborator = accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	return SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:ForgeExtensionList', async () => {
		try {
			if (fg('use_new_forge_extension_list_call')) {
				const extensions = await preloadForgeApps({
					cloudId,
					locale,
					types: [moduleType],
					context: {
						spaceKey,
						contentId,
					},
					includeHidden: false,
				});

				if (extensions) {
					setWindowExtensions(moduleType, extensions, undefined);
				}
			} else {
				// eslint-disable-next-line check-preloaders/no-promise-all
				const [staticContext, extensions] = await Promise.all([
					preloadUseStaticContext({ spaceKey }),
					preloadExtensions({
						spaceKey,
						cloudId,
						activationId,
						locale,
						hasAccessNarrowingSupport,
						expandAppOwner,
						moduleType,
						queryOptions,
						skip: isAnonymous || isExternalCollaborator,
						skipFilter,
					}),
				]);

				if (extensions) {
					await filterExtensions(staticContext, extensions, contentId, spaceKey, moduleType);
				}
			}
		} catch (e) {
			ssrPrepareErrorsClient.submitError(
				new Error(`Filed to get forge extension list: ${e.message}`),
			);
		}
	});
}
